import React, { useEffect, useRef, useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import _ from 'lodash';

interface StarRatingProps {
  name?: string;
  canEdit?: boolean;
  rating?: number;
  fontSize?: string;
  handleRate?: (e: React.MouseEvent, rating: number) => void;
}

function getMousePosition(e) {
  return e.pageX;
}

const StarRating = ({
  canEdit = false,
  fontSize = '1.2em',
  handleRate,
  name,
  rating: ratingProp,
}: StarRatingProps) => {
  const starRatingRef = useRef(null);
  const [clientRect, setClientRect] = useState(null);
  const [tempX, setTempX] = useState(null);
  const [tempRating, setTempRating] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (canEdit) {
      window.addEventListener('resize', handleResize);
      const gotClientRect = getClientRect();
      setClientRect(gotClientRect);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [canEdit]);

  const handleResize = (e) => {
    e.preventDefault();
    const gotClientRect = getClientRect();
    setClientRect(gotClientRect);
  };

  const getClientRect = () => {
    const starRating = starRatingRef.current;
    const rect = starRating.getBoundingClientRect();
    return rect;
  };

  const onClick = (e) => {
    handleRate(e, tempRating);
  };

  const onMove = (e, currentX) => {
    const rating = getRating(currentX);
    setTempX(currentX);
    setTempRating(rating);
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    const currentX = getMousePosition(e);
    onMove(e, currentX);
  };

  const removeEvents = () => {
    document.removeEventListener('mousemove', onMouseMove);
  };

  const getRating = (currentX) => {
    const width = clientRect.width;
    const start = clientRect.left;
    return Math.ceil(((currentX - start) / width) * 5);
  };

  const onMouseEnter = (e) => {
    document.addEventListener('mousemove', onMouseMove, false);
    const newTempX = getMousePosition(e);
    const newRating = getRating(tempX);
    setTempX(newTempX);
    setTempRating(newRating);
    setMoving(true);
    e.stopPropagation();
    e.preventDefault();
  };

  const onMouseLeave = (e) => {
    e.stopPropagation();
    e.preventDefault();
    removeEvents();
    setMoving(false);
    setTempX(null);
    setTempRating(null);
  };

  let rating;
  if (moving) {
    rating = tempRating;
  } else {
    rating = ratingProp;
  }
  const range = _.range(5);
  const stars = range.map((num) => {
    let starClass = 'h-5 w-5 ';
    const fontColor =
      Math.round(rating - num) > 0 ? 'text-beach' : ' text-default';
    starClass = starClass + fontColor;
    const key = `star-rating-${name}-${num}`;
    return <StarIcon key={key} className={starClass} />;
  });
  const inlineStyle = { fontSize: fontSize };
  if (canEdit) {
    return (
      <div
        ref={starRatingRef}
        style={inlineStyle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        <div className="foreground">
          <span>{stars}</span>
        </div>
      </div>
    );
  } else {
    return (
      <div style={inlineStyle}>
        <div className="foreground">
          <div className="flex items-center">{stars}</div>
        </div>
      </div>
    );
  }
};

export default StarRating;
